import { merge } from "lodash-es";
import { storeToRefs } from "pinia";
import { computed, toRefs } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import {
  getContactInfluencerFields,
  getContactOutletFields,
  getContactSortOptions,
} from "shared/helpers/outreach";
import { Contact, OutreachLocation } from "shared/resources";
import { useOutreachContactsStore } from "shared/stores/outreachContacts";
import { useUserStore } from "shared/stores/user";

export default function contactsSearchFiltering(options) {
  const contactOptions = toRefs(options);

  const outreachContactsStore = useOutreachContactsStore();

  const { getFilters, searchTab, sortBy, sortDirection } = storeToRefs(
    outreachContactsStore
  );

  const { setFilterLocations } = outreachContactsStore;

  const { currentUser } = storeToRefs(useUserStore());

  function getState(customState) {
    return {
      ...getFilters.value,
      ...customState,
    };
  }

  const contactSortOptions = computed(() => getContactSortOptions());

  const contactOutletFields = computed(() => getContactOutletFields());

  const contactSearchType = computed(
    () => contactOptions.contactType?.value || searchTab.value
  );

  const selectedSort = computed(() =>
    contactSortOptions.value
      .find((optionSet) => optionSet.type === contactSearchType.value)
      .options.find(
        (option) => option.name === sortBy.value[contactSearchType.value]
      )
  );

  const sortLabel = computed(() => {
    const label = {
      influencer: getLocaleText("global.journalist"),
      outlet: getLocaleText("global.outlet"),
      all: "",
    };

    return `${label[contactSearchType.value]} ${
      selectedSort.value.label
    }`.trim();
  });

  function getTalkingAboutFilter(state) {
    if (state.filterTalkingAboutTags.length) {
      const talkingAboutFilter = {
        keywords: state.filterTalkingAboutTags.map((tag) =>
          encodeURIComponent(`{${tag}}`)
        ),
        operator: state.filterTalkingAboutOperator,
        contact_search_type: contactSearchType.value,
      };

      if (selectedSort.value.name === "content_relevance") {
        talkingAboutFilter.talking_about_relevancy_sort =
          sortDirection.value[contactSearchType.value];
      }

      return talkingAboutFilter;
    }

    return null;
  }

  function getFormattedLocations(state) {
    if (!state.filterLocations.length) {
      return null;
    }

    return {
      countries: state.filterLocations
        .filter((location) => location.locationType === "Country")
        .map((location) => location.name),
      states: state.filterLocations
        .filter((location) => location.locationType === "State")
        .map((location) => location.name),
      cities: state.filterLocations.filter(
        (location) => location.locationType === "City"
      ),
    };
  }

  function getKeywordsMultiMatchFilter(query, fields, prefix) {
    let hasNameField = false;

    const searchFields = fields.flatMap((field) => {
      if (field.name === "name_analysed") {
        hasNameField = true;

        return `${prefix}${field.name}^2`;
      }

      if (field.attributes) {
        return field.attributes.map((attr) => `${prefix}${field.name}.${attr}`);
      }

      return `${prefix}${field.name}`;
    });

    const keywordFilters = [
      {
        multi_match: {
          query,
          fields: searchFields,
          type: "bool_prefix",
          analyzer: "standard",
          operator: "and",
          fuzziness: 0,
          tie_breaker: 0.3,
        },
      },
    ];

    if (hasNameField) {
      keywordFilters.push({
        bool: {
          minimum_should_match: 1,
          should: [
            {
              match_phrase: {
                [`${prefix}name`]: {
                  query,
                  boost: 5,
                },
              },
            },
            {
              match: {
                [`${prefix}name_analysed`]: {
                  query,
                  operator: "and",
                  fuzziness: "AUTO",
                  prefix_length: 1,
                },
              },
            },
            {
              wildcard: {
                [`${prefix}name`]: {
                  value: `${query}*`,
                },
              },
            },
          ],
        },
      });
    }

    return keywordFilters;
  }

  function getOutletKeywordsFilter(state, { nested = false } = {}) {
    if (!state.keywordQuery || !state.keywordOutletFields.length) {
      return [];
    }

    const prefix =
      contactSearchType.value === "influencer" || nested ? "outlets." : "";

    const fields = state.keywordOutletFields.length
      ? contactOutletFields.value.filter((field) =>
          state.keywordOutletFields.includes(field.name)
        )
      : contactOutletFields.value;

    return getKeywordsMultiMatchFilter(state.keywordQuery, fields, prefix);
  }

  function getInfluencerKeywordsFilter(state, { nested = false } = {}) {
    if (!state.keywordQuery || !state.keywordInfluencerFields.length) {
      return [];
    }

    const prefix =
      contactSearchType.value === "outlet" || nested ? "influencers." : "";

    const contactInfluencerFields = getContactInfluencerFields();

    const fields = state.keywordInfluencerFields.length
      ? contactInfluencerFields.filter((field) =>
          state.keywordInfluencerFields.includes(field.name)
        )
      : contactInfluencerFields;

    return getKeywordsMultiMatchFilter(state.keywordQuery, fields, prefix);
  }

  function cityFilterClause(key, cities) {
    const terms = [];

    cities.forEach((city) => {
      const stateName = city.parentName;

      const term = {
        bool: {
          must: [
            {
              term: {
                [`${key}.state`]: { value: encodeURIComponent(stateName) },
              },
            },
            {
              term: {
                [`${key}.city`]: { value: encodeURIComponent(city.name) },
              },
            },
          ],
        },
      };

      terms.push(term);
    });

    return terms;
  }

  function getLocationTerms(componentState, prefix = "") {
    const locations = getFormattedLocations(componentState);

    let locationTerms = [];

    // replicates what it was doing before
    if (locations.countries.length) {
      const countries = locations.countries.map((locationCountry) =>
        encodeURIComponent(locationCountry)
      );

      locationTerms.push({
        terms: { [`${prefix}location`]: countries },
      });
    }

    // also search for states
    if (locations.states.length) {
      const states = locations.states.map((locationState) =>
        encodeURIComponent(locationState)
      );

      if (contactSearchType.value === "influencer") {
        // match influencer address state only
        locationTerms.push({
          terms: { "influencer_addresses.state": states },
        });
      } else if (contactSearchType.value === "outlet") {
        // match outlet address state only
        locationTerms.push({
          terms: { "outlet_addresses.state": states },
        });
      } else {
        // the all tab so we should match both the outlet and the influencer's address state
        locationTerms.push({
          terms: { [`${prefix}outlet_addresses.state`]: states },
        });

        locationTerms.push({
          terms: { [`${prefix}influencer_addresses.state`]: states },
        });
      }
    }

    // now searching for cities
    if (locations.cities.length) {
      const { cities } = locations;

      if (contactSearchType.value === "influencer") {
        locationTerms = locationTerms.concat(
          cityFilterClause("influencer_addresses", cities)
        );
      } else if (contactSearchType.value === "outlet") {
        // match outlet address state only
        locationTerms = locationTerms.concat(
          cityFilterClause("outlet_addresses", cities)
        );
      } else {
        // the all tab so we should match both the outlet and the influencer's address state
        locationTerms = locationTerms.concat(
          cityFilterClause(`${prefix}outlet_addresses`, cities)
        );

        locationTerms = locationTerms.concat(
          cityFilterClause(`${prefix}influencer_addresses`, cities)
        );
      }
    }

    return locationTerms;
  }

  function getOutletTermsFilters(state, { nested = false } = {}) {
    const terms = [];

    const prefix =
      contactSearchType.value === "influencer" || nested ? "outlets." : "";

    if (state.filterLocations.length && !nested) {
      terms.push({
        bool: {
          minimum_should_match: 1,
          should: getLocationTerms(state, prefix),
        },
      });
    }

    if (prefix === "") {
      terms.push({
        terms: {
          monitoring_only_flag: [false],
        },
      });
    }

    if (state.filterTargetAreas.length) {
      terms.push({
        terms: {
          [`${prefix}target_area_location_ids`]: state.filterTargetAreas.map(
            (country) => country.id
          ),
        },
      });
    }

    if (state.filterMediaTypes.length) {
      terms.push({
        terms: {
          [`${prefix}outlet_type.name`]: state.filterMediaTypes.map(
            (type) => type.name
          ),
        },
      });
    }

    if (state.filterFrequencies.length) {
      terms.push({
        terms: {
          [`${prefix}frequency`]: state.filterFrequencies.map((value) => value),
        },
      });
    }

    if (state.filterCoverages.length) {
      terms.push({
        terms: {
          [`${prefix}coverage`]: state.filterCoverages.map((value) => value),
        },
      });
    }

    if (state.filterContentTypes.length) {
      terms.push({
        terms: {
          [`${prefix}content_types`]: state.filterContentTypes.map(
            (value) => value
          ),
        },
      });
    }

    if (state.filterTopics.length && !nested) {
      terms.push({
        terms: {
          [`${prefix}topics.id`]: state.filterTopics.map((topic) => topic.id),
        },
      });
    }

    if (state.filterLanguages.length && !nested) {
      terms.push({
        terms: {
          languages: state.filterLanguages,
        },
      });
    }

    if (state.filterSocials.length && !nested) {
      terms.push({
        terms: {
          social_types: state.filterSocials.map((social) => social),
        },
      });
    }

    if (state.filterDesignatedMarketAreas.length) {
      if (contactSearchType.value !== "influencer" && !nested) {
        // match outlet address state only
        terms.push({
          terms: {
            [`outlet_addresses.dma_id`]: state.filterDesignatedMarketAreas.map(
              (dma) => dma.dmaId
            ),
          },
        });
      }
    }

    return terms;
  }

  function getInfluencerTermsFilters(state, { nested = false } = {}) {
    const terms = [];

    const prefix =
      contactSearchType.value === "outlet" || nested ? "influencers." : "";

    if (state.filterDesignatedMarketAreas.length && !nested) {
      if (contactSearchType.value !== "outlet") {
        // match influencer address state only
        terms.push({
          terms: {
            [`influencer_addresses.dma_id`]:
              state.filterDesignatedMarketAreas.map((dma) => dma.dmaId),
          },
        });
      }
    }

    if (state.filterLocations.length) {
      terms.push({
        bool: {
          minimum_should_match: 1,
          should: getLocationTerms(state, prefix),
        },
      });
    }

    if (state.filterTopics.length) {
      terms.push({
        terms: {
          [`${prefix}topics.id`]: state.filterTopics.map((topic) => topic.id),
        },
      });
    }

    if (state.filterLanguages.length) {
      terms.push({
        terms: {
          [`${prefix}languages`]: state.filterLanguages,
        },
      });
    }

    if (state.filterSocials.length) {
      terms.push({
        terms: {
          [`${prefix}social_types`]: state.filterSocials.map(
            (social) => social
          ),
        },
      });
    }

    return terms;
  }

  function getRangeFilter(state, { nested = false } = {}) {
    if (!state.filterCirculation.enabled) {
      return [];
    }

    const prefix =
      contactSearchType.value === "influencer" || nested ? "outlets." : "";

    return [
      {
        range: {
          [`${prefix}circulation`]: {
            ...(state.filterCirculation.lte
              ? { lte: state.filterCirculation.lte }
              : {}),
            ...(state.filterCirculation.gte
              ? { gte: state.filterCirculation.gte }
              : {}),
          },
        },
      },
    ];
  }

  function getInfluencerNestedFilters(state) {
    return merge({
      must: [
        ...getOutletTermsFilters(state, { nested: true }),
        ...getRangeFilter(state, { nested: true }),
      ],
    });
  }

  function getNestedOutletKeywordFilters(state) {
    const nestedQuery = {
      nested: {
        path: "influencers",
        ignore_unmapped: true,
        query: {
          bool: {},
        },
      },
    };

    const influencerKeywordFilters = getInfluencerKeywordsFilter(state, {
      nested: true,
    });

    if (Object.keys(influencerKeywordFilters).length) {
      Object.assign(nestedQuery.nested.query.bool, {
        minimum_should_match: 1,
        should: influencerKeywordFilters,
      });
    }

    return nestedQuery;
  }

  function getNestedInfluencerKeywordFilters(state) {
    const nestedQuery = {
      nested: {
        path: "outlets",
        ignore_unmapped: true,
        query: {
          bool: {},
        },
      },
    };

    const outletKeywordFilters = getOutletKeywordsFilter(state, {
      nested: true,
    });

    if (Object.keys(outletKeywordFilters).length) {
      Object.assign(nestedQuery.nested.query.bool, {
        must: [
          {
            terms: {
              [`outlets.monitoring_only_flag`]: [false],
            },
          },
        ],
      });

      Object.assign(nestedQuery.nested.query.bool, {
        minimum_should_match: 1,
        should: outletKeywordFilters,
      });
    }

    return nestedQuery;
  }

  function getInfluencerOutletFilters(state) {
    const nestedQuery = {
      nested: {
        path: "outlets",
        ignore_unmapped: true,
        query: {
          bool: {},
        },
      },
    };

    const influencerNestedFilters = getInfluencerNestedFilters(state);

    const hasInfluencerNestedFilters = Object.keys(
      influencerNestedFilters.must
    ).length;

    if (
      hasInfluencerNestedFilters &&
      (contactSearchType.value === "influencer" ||
        contactSearchType.value === "all")
    ) {
      Object.assign(nestedQuery.nested.query.bool, influencerNestedFilters);
    }

    let bool = {};

    if (Object.keys(nestedQuery.nested.query.bool).length) {
      bool = { ...nestedQuery };
    }

    return bool;
  }

  function getOutletFilters(state) {
    const filters = {
      query: {
        bool: {
          must: [
            {
              terms: {
                type: ["outlet"],
              },
            },
            ...getOutletTermsFilters(state, { nested: false }),
            ...getRangeFilter(state, { nested: false }),
          ],
          must_not: [
            {
              terms: {
                parent_relationship: ["desk"],
              },
            },
          ],
        },
      },
    };

    const outletKeywordFilters = getOutletKeywordsFilter(state);

    const nestedOutletKeywordFilters = getNestedOutletKeywordFilters(state);

    const matchingClause = {
      bool: {
        minimum_should_match: 1,
        should: [],
      },
    };

    if (outletKeywordFilters.length) {
      matchingClause.bool.should.push(...outletKeywordFilters);
    }

    if (nestedOutletKeywordFilters.nested.query.bool.should?.length) {
      matchingClause.bool.should.push({ ...nestedOutletKeywordFilters });
    }

    if (matchingClause.bool.should.length) {
      filters.query.bool.must.push(matchingClause);
    }

    const talkingAboutFilter = getTalkingAboutFilter(state);

    if (talkingAboutFilter) {
      filters.content_talking_about = {
        eq: talkingAboutFilter,
      };
    }

    return filters;
  }

  function getInfluencerFilters(state) {
    const filters = {
      query: {
        bool: {
          must: [
            {
              terms: {
                type: ["influencer"],
              },
            },
            ...getInfluencerTermsFilters(state),
          ],
        },
      },
    };

    const influencerKeywordFilters = getInfluencerKeywordsFilter(state);

    const nestedInfluencerKeywordFilters =
      getNestedInfluencerKeywordFilters(state);

    const matchingClause = {
      bool: {
        minimum_should_match: 1,
        should: [],
      },
    };

    if (influencerKeywordFilters.length) {
      matchingClause.bool.should.push(...influencerKeywordFilters);
    }

    if (nestedInfluencerKeywordFilters.nested.query.bool.should?.length) {
      matchingClause.bool.should.push({ ...nestedInfluencerKeywordFilters });
    }

    if (matchingClause.bool.should.length) {
      filters.query.bool.must.push(matchingClause);
    }

    const nestedFilters = getInfluencerOutletFilters(state);
    const hasNestedFilters = Object.keys(nestedFilters).length;

    if (hasNestedFilters) {
      filters.query.bool.must.push({ ...nestedFilters });
    }

    const talkingAboutFilter = getTalkingAboutFilter(state);

    if (talkingAboutFilter) {
      filters.content_talking_about = {
        eq: talkingAboutFilter,
      };
    }

    return filters;
  }

  function getAllFilters(state) {
    const { query: outletQuery, ...outletFilters } = getOutletFilters(state);

    const { query: influencerQuery, ...influencerFilters } =
      getInfluencerFilters(state);

    const filters = {
      query: {
        bool: {
          must: [
            {
              bool: {
                minimum_should_match: 1,
                should: [
                  {
                    ...outletQuery,
                  },
                  {
                    ...influencerQuery,
                  },
                ],
              },
            },
          ],
        },
      },
      ...outletFilters,
      ...influencerFilters,
    };

    return filters;
  }

  function getContactFilters(customState) {
    const state = getState(customState);

    if (contactSearchType.value === "all" || state.searchTab === "all") {
      return getAllFilters(state);
    }

    if (contactSearchType.value === "outlet") {
      return getOutletFilters(state);
    }

    if (contactSearchType.value === "influencer") {
      return getInfluencerFilters(state);
    }

    return getAllFilters(state);
  }

  const contactIncludes = computed(() => {
    const influencerIncludes = [
      "influencerAddresses",
      "influencerOutreachTopics",
      "twitterUser",
      "outlets",
      "outlet",
    ];

    const outletIncludes = [
      "outletAddresses",
      "outletType",
      "outletOutreachTopics",
    ];

    if (contactSearchType.value === "influencer") {
      return influencerIncludes;
    }

    if (contactSearchType.value === "outlet") {
      return outletIncludes;
    }

    return [...outletIncludes, ...influencerIncludes];
  });

  const contactSelectExtra = computed(() => {
    if (contactSearchType.value === "outlet") return {};

    return { influencers: ["businessCardTitles", "dominantFacebookReaction"] };
  });

  const contactsOrder = computed(() => {
    if (
      ["content_relevance", "relevance"].includes(selectedSort.value.name) ||
      selectedSort.value.name === "relevance"
    ) {
      return {};
    }

    return {
      [selectedSort.value.name]: sortDirection.value[contactSearchType.value],
    };
  });

  const sortDirectionIcon = computed(() =>
    sortDirection.value[contactSearchType.value] === "desc"
      ? "fas fa-sort-amount-down"
      : "fas fa-sort-amount-up"
  );

  const sortDirectionTooltip = computed(() => {
    if (selectedSort.value.name === "relevance") {
      return null;
    }

    return sortDirection.value[contactSearchType.value] === "desc"
      ? getLocaleText("global.descending")
      : getLocaleText("global.ascending");
  });

  const activeFilterCount = computed(() => {
    const state = getState();

    const allFilters = [
      state.filterTopics.length,
      state.filterLocations.length,
      state.filterTalkingAbout,
      state.filterTargetAreas.length,
      state.filterLanguages.length,
      state.filterSocials.length,
      state.filterCoverages.length,
      state.filterMediaTypes.length,
      state.filterCirculation.gte || state.filterCirculation.lte,
      state.filterFrequencies.length,
      state.filterContentTypes.length,
    ];

    return allFilters.filter(Boolean).length;
  });

  async function getContactTotals(state) {
    const { query, ...filters } = getContactFilters(state);

    const { meta } = await Contact.where({
      query: JSON.stringify(query),
      ...filters,
    })
      .page(0)
      .stats({
        total: "contacts",
      })
      .all();

    const { contacts } = meta.stats.total;

    return contacts;
  }

  async function setDefaultLocationFilters() {
    const state = getState();

    const outreachCountries = currentUser.value.outreach_countries;

    if (!state.filterLocations.length) {
      const userCountries = (
        await OutreachLocation.selectExtra(["selfAndParentNames"])
          .where({ name: outreachCountries, locationType: "Country" })
          .all()
      ).data;

      setFilterLocations(userCountries);
    }
  }

  return {
    getContactFilters,
    contactIncludes,
    contactSelectExtra,
    contactsOrder,
    sortLabel,
    sortDirectionIcon,
    sortDirectionTooltip,
    getContactTotals,
    setDefaultLocationFilters,
    activeFilterCount,
  };
}
